import useSWR from "swr";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import logger from "../../../lib/logger";
import { isSimbManagedColumnTitle } from "../../../utils/simbManagedColumns";

type ColumnData = {
  title: string;
  type: string;
};

type Board = {
  id: string;
  columns: ColumnData[];
};

type SharedColumnsTourData = {
  matchedColumns: ColumnData[];
  unmatchedColumns: ColumnData[];
};

const getColumnKey = (column: ColumnData) =>
  `${column.title.trim().toLowerCase()}__${column.type.trim().toLowerCase()}`;

const getColumnsMap = (columns: ColumnData[]) =>
  columns.reduce(
    (acc, column) => {
      acc[getColumnKey(column)] = column;
      return acc;
    },
    {} as Record<string, ColumnData>,
  );

const fetchSharedColumns = async (boardIds: number[]): Promise<SharedColumnsTourData> => {
  if (boardIds.length < 2) {
    return { matchedColumns: [], unmatchedColumns: [] };
  }

  try {
    const boardDetails: Board[] = await MondayApiQueryInstance.getBoardColumns(boardIds);
    const [sourceBoardId, targetBoardId] = boardIds;

    const sourceColumns =
      boardDetails
        .find((board) => parseInt(board.id, 10) === sourceBoardId)
        ?.columns.filter((column) => !isSimbManagedColumnTitle(column.title)) ?? [];

    const targetColumns =
      boardDetails
        .find((board) => parseInt(board.id, 10) === targetBoardId)
        ?.columns.filter((column) => !isSimbManagedColumnTitle(column.title)) ?? [];

    const sourceColumnsMap = getColumnsMap(sourceColumns);
    const targetColumnsMap = getColumnsMap(targetColumns);

    const matchedColumns = Object.keys(sourceColumnsMap)
      .filter((columnKey) => !!targetColumnsMap[columnKey])
      .map((columnKey) => sourceColumnsMap[columnKey]);

    const unmatchedColumns = [
      ...Object.keys(sourceColumnsMap)
        .filter((columnKey) => !targetColumnsMap[columnKey])
        .map((columnKey) => sourceColumnsMap[columnKey]),
      ...Object.keys(targetColumnsMap)
        .filter((columnKey) => !sourceColumnsMap[columnKey])
        .map((columnKey) => targetColumnsMap[columnKey]),
    ];

    return {
      matchedColumns,
      unmatchedColumns,
    };
  } catch (error: any) {
    logger.error("Error fetching shared columns", { error, boardIds });
    throw error;
  }
};

const useGetSharedColumnsTourModal = (boardIds: number[], tourState: boolean) => {
  const hasValidBoardIds =
    boardIds.length === 2 && boardIds.every((boardId) => Number.isFinite(boardId) && boardId > 0);

  const { data, error, isValidating, mutate } = useSWR(
    hasValidBoardIds && tourState ? `sharedColumnsTour:${boardIds}` : null,
    () => fetchSharedColumns(boardIds),
    {
      revalidateOnFocus: false,
    },
  );

  return {
    sharedColumnsTour: data?.matchedColumns || [],
    unmatchedColumnsTour: data?.unmatchedColumns || [],
    isLoading: !data && !error,
    isValidating,
    isError: !!error,
    error,
    mutate,
  };
};

export default useGetSharedColumnsTourModal;
